import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTraffic } from '../context/TrafficContext';
import {
  detectDisclosures, summarize,
  SEVERITY_COLOR, SEVERITY_LABEL, MATRIX_TYPES,
} from '../validation/dataDisclosures';
import { postDisclosures } from '../api/disclosures';

/* ──────────────────────────────────────────────────────────────────────────
   DisclosuresPage — live "flag them as they fire" view.
   Reads liveTrafficGroups from TrafficContext, runs the shared detection
   module over every request, and renders:
     • summary tiles (scanned / flagged / vendors / high-risk)
     • rule-based findings strip (F1…F6, only those firing)
     • a live newest-first feed of flagged requests (severity-coloured)
     • a collapsible vendor × data-type matrix

   WP3e (docs/frontend-validation-port-spec.md §4): detectDisclosures/summarize
   now have a server counterpart at proxy-server/disclosures/dataDisclosures.js,
   posted to via api/disclosures.js's postDisclosures(). This page prefers the
   server's answer (debounced 400ms on every `rows` change, mirroring
   BidHealthView.jsx's pattern) and falls back to the still-imported local
   detectDisclosures/summarize on any transport failure or incomplete
   response — never merging the two. The local copies stay imported and are
   the fallback, not dead code.
   ────────────────────────────────────────────────────────────────────────── */

const SEV_BG = { high: 'var(--error-bg)', med: 'var(--warn-bg)', consent: 'var(--info-bg)', low: 'var(--surface)' };
const FINDING_SEV_COLOR = { High: '#e5484d', Medium: '#f5a524', Low: '#8b8d98' };

/**
 * resolveDisclosuresSource — all-or-nothing server/local resolver, mirroring
 * BidHealthView.jsx's resolveBidHealthSource. A server payload is only used
 * when it is a complete, index-aligned answer for the CURRENT rows array;
 * anything short of that (unreachable proxy, shape drift, a stale response
 * for a rows array that has since changed length) falls back to computing
 * the whole output set locally — never a partial merge of both.
 *
 * @param {{perRow: object[], summary: object}|null} serverPayload
 * @param {'pending'|'server'|'local'|'empty'} source
 * @param {object[]} rows - the exact array that was (or would be) posted
 * @param {{detected: () => object[], summary: () => object}} local - lazy
 *   thunks so the browser never pays local-computation cost when the server
 *   path is in use (WP1c perf pattern)
 */
export function resolveDisclosuresSource(serverPayload, source, rows, local) {
  const complete = source === 'server'
    && !!serverPayload
    && Array.isArray(serverPayload.perRow)
    && serverPayload.perRow.length === rows.length
    && !!serverPayload.summary
    && Array.isArray(serverPayload.summary.vendors)
    && Array.isArray(serverPayload.summary.findings);

  if (!complete) {
    return { usingServer: false, detected: local.detected(), summary: local.summary() };
  }

  const detected = rows
    .map((r, i) => ({ row: r, d: serverPayload.perRow[i] }))
    .filter((x) => x.d && Array.isArray(x.d.flags) && x.d.flags.length > 0);

  return { usingServer: true, detected, summary: serverPayload.summary };
}

function timeOf(row) {
  const t = row.startedAt || row.timestamp;
  if (t == null) return '';
  const d = typeof t === 'number' ? new Date(t) : new Date(t);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

export default function DisclosuresPage() {
  const { liveTrafficGroups, clearSignal } = useTraffic();

  const groupKeys = Object.keys(liveTrafficGroups || {});
  const [device, setDevice] = useState('all');
  const [sevFilter, setSevFilter] = useState('all'); // all | high | med | consent | low
  const [vendorFilter, setVendorFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [paused, setPaused] = useState(false);
  const [showMatrix, setShowMatrix] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const frozenRef = useRef(null);

  const [serverPayload, setServerPayload] = useState(null);
  const [disclosuresSource, setDisclosuresSource] = useState('pending'); // 'pending'|'server'|'local'|'empty'
  const requestSeqRef = useRef(0);

  // reset frozen snapshot when user clears all
  useEffect(() => {
    frozenRef.current = null;
    setExpanded(null);
    setServerPayload(null);
    setDisclosuresSource('pending');
  }, [clearSignal]);

  // flatten selected device(s)
  const liveRows = useMemo(() => {
    const groups = liveTrafficGroups || {};
    const picked = device === 'all'
      ? Object.values(groups).flat()
      : (groups[device] || []);
    return picked;
  }, [liveTrafficGroups, device]);

  // when paused, hold the last snapshot so the feed stops moving
  const rows = useMemo(() => {
    if (paused) {
      if (!frozenRef.current) frozenRef.current = liveRows;
      return frozenRef.current;
    }
    frozenRef.current = null;
    return liveRows;
  }, [paused, liveRows]);

  // debounced POST to the server, mirroring BidHealthView.jsx's pattern —
  // re-post 400ms after the last `rows` change, so a fast-moving live feed
  // doesn't fire one request per WS event.
  useEffect(() => {
    if (!rows.length) {
      setDisclosuresSource('empty');
      setServerPayload(null);
      return undefined;
    }
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setDisclosuresSource((s) => (s === 'server' ? s : 'pending'));
    const timer = setTimeout(() => {
      postDisclosures(rows)
        .then((payload) => {
          if (requestSeqRef.current !== seq) return; // superseded by a later rows change
          if (payload && Array.isArray(payload.perRow) && payload.perRow.length === rows.length && payload.summary) {
            setServerPayload(payload);
            setDisclosuresSource('server');
          } else {
            setDisclosuresSource('local');
          }
        })
        .catch(() => {
          if (requestSeqRef.current !== seq) return;
          setDisclosuresSource('local');
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [rows]);

  // resolve server vs. local — all-or-nothing, never merged (see
  // resolveDisclosuresSource above). Local thunks are lazy so the browser
  // never pays the local-computation cost while the server path is live.
  const { detected, summary, usingServer } = useMemo(
    () => resolveDisclosuresSource(serverPayload, disclosuresSource, rows, {
      detected: () => rows.map((r) => ({ row: r, d: detectDisclosures(r) })).filter((x) => x.d.flags.length > 0),
      summary: () => summarize(rows),
    }),
    [serverPayload, disclosuresSource, rows],
  );

  const vendorOptions = useMemo(
    () => [...new Set(summary.vendors.map((v) => v.vendor))].sort(),
    [summary.vendors],
  );

  // feed = flagged rows, newest first, after filters
  const feed = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = detected.filter(({ d }) => {
      if (sevFilter !== 'all' && !d.flags.some((f) => f.severity === sevFilter)) return false;
      if (vendorFilter !== 'all' && d.vendor !== vendorFilter) return false;
      if (q) {
        const hay = `${d.vendor} ${d.host} ${d.flags.map((f) => f.type + f.value).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // liveTrafficGroups is newest-first already (MainApp unshifts); keep order
    return list.slice(0, 400);
  }, [detected, sevFilter, vendorFilter, query]);

  const highRiskVendors = summary.vendors.filter(
    (v) => v.topSeverity === 'high' && !['First-party Content', 'First-party CDN'].includes(v.category),
  ).length;
  const tokenCount = Object.values(summary.tokenSources).reduce((a, b) => a + b, 0);

  const tile = (label, value, color) => (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10,
      padding: '12px 16px', minWidth: 110,
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--ink)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: '18px 22px 60px', maxWidth: 1240, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>🛡️ Data Disclosures</h1>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>
          live third-party tracker &amp; PII flagging
        </span>
        {!usingServer && disclosuresSource === 'local' && (
          <span
            title="The proxy could not be reached, so disclosures were detected in the browser. They may differ from a server-side sign-off report."
            style={{
              marginLeft: 'auto', fontSize: 11, color: '#854F0B', border: '1px solid #E5C07B', borderRadius: 4, padding: '1px 6px',
            }}
          >
            computed locally — proxy unreachable
          </span>
        )}
        <span style={{
          marginLeft: !usingServer && disclosuresSource === 'local' ? 8 : 'auto', fontSize: 11, fontWeight: 600,
          color: paused ? '#d97706' : '#22c55e',
        }}>
          {paused ? '⏸ paused' : '● live'}
        </span>
      </div>

      {/* Tiles */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0 8px' }}>
        {tile('requests scanned', summary.meta.scanned)}
        {tile('flagged', summary.meta.flagged, '#e5484d')}
        {tile('vendors', summary.meta.vendors)}
        {tile('high-risk vendors', highRiskVendors, highRiskVendors ? '#e5484d' : undefined)}
        {tile('identity tokens', tokenCount, tokenCount ? '#f5a524' : undefined)}
      </div>

      {summary.meta.scanned === 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px dashed var(--line)', borderRadius: 10,
          padding: '28px 20px', textAlign: 'center', color: 'var(--muted)', marginTop: 16,
        }}>
          No live traffic yet. Connect a device and browse the app — flagged disclosures will stream in here.
        </div>
      )}

      {/* Findings strip */}
      {summary.findings.length > 0 && (
        <div style={{ margin: '18px 0 6px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Findings firing now</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: 10 }}>
            {summary.findings.map((f) => (
              <div key={f.id} style={{
                background: 'var(--surface)', border: '1px solid var(--line)',
                borderLeft: `4px solid ${FINDING_SEV_COLOR[f.severity]}`, borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    background: FINDING_SEV_COLOR[f.severity], color: '#fff', fontSize: 10,
                    fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                  }}>{f.severity}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>{f.id}</span>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, margin: '6px 0 4px' }}>{f.title}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{f.vendors.join(' · ')}</div>
                {f.example && (
                  <div style={{
                    marginTop: 6, fontFamily: 'monospace', fontSize: 10.5,
                    background: '#0f1117', color: '#9ad0ff', borderRadius: 6, padding: '5px 8px',
                    overflowX: 'auto', whiteSpace: 'nowrap',
                  }}>
                    {f.example.param} = <span style={{ color: '#ffd479' }}>{f.example.value}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        margin: '20px 0 10px', position: 'sticky', top: 0, zIndex: 5,
        background: 'var(--bg)', padding: '6px 0',
      }}>
        {groupKeys.length > 1 && (
          <select value={device} onChange={(e) => setDevice(e.target.value)} className="dd-ctl">
            <option value="all">All devices</option>
            {groupKeys.map((k) => <option key={k} value={k}>{k} ({liveTrafficGroups[k].length})</option>)}
          </select>
        )}
        <select value={sevFilter} onChange={(e) => setSevFilter(e.target.value)} className="dd-ctl">
          <option value="all">All severities</option>
          <option value="high">High — identifier/PII</option>
          <option value="med">Medium — pseudonymous</option>
          <option value="consent">Consent signals</option>
          <option value="low">Low — context</option>
        </select>
        <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} className="dd-ctl">
          <option value="all">All vendors</option>
          {vendorOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <input
          className="dd-ctl" placeholder="Search flag / value / host…"
          value={query} onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 200 }}
        />
        <button className="dd-ctl" onClick={() => setPaused((p) => !p)}
          style={{ cursor: 'pointer', fontWeight: 600, color: paused ? '#d97706' : 'inherit' }}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>
        <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
          {feed.length} flagged shown
        </span>
      </div>

      {/* Live feed */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surface)', color: 'var(--muted)', fontSize: 11 }}>
              <th style={th}>Time</th>
              <th style={th}>Vendor</th>
              <th style={{ ...th, width: 46 }}>Sev</th>
              <th style={th}>Data disclosed</th>
              <th style={th}>Request</th>
            </tr>
          </thead>
          <tbody>
            {feed.map(({ row, d }, i) => {
              const key = row.id || `${i}-${row.url}`;
              const open = expanded === key;
              return (
                <React.Fragment key={key}>
                  <tr
                    onClick={() => setExpanded(open ? null : key)}
                    style={{
                      borderTop: '1px solid var(--line)', cursor: 'pointer',
                      background: open ? 'var(--surface)' : 'transparent',
                    }}
                  >
                    <td style={{ ...td, color: 'var(--muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {timeOf(row)}
                    </td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: SEVERITY_COLOR[d.topSeverity] || '#8b8d98', marginRight: 6,
                      }} />
                      <b>{d.vendor}</b>
                      <div style={{ color: 'var(--muted)', fontSize: 10 }}>{d.category}</div>
                    </td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5,
                        color: SEVERITY_COLOR[d.topSeverity], background: SEV_BG[d.topSeverity],
                      }}>{d.topSeverity}</span>
                    </td>
                    <td style={td}>
                      {d.flags.map((f, j) => (
                        <span key={j} title={`${f.param} = ${f.value}`} style={{
                          display: 'inline-block', fontSize: 10, margin: '1px 3px 1px 0',
                          padding: '1px 6px', borderRadius: 5,
                          color: SEVERITY_COLOR[f.severity], background: SEV_BG[f.severity],
                        }}>
                          {f.type}{f.value === '(EMPTY)' ? ' ⚠' : ''}
                        </span>
                      ))}
                    </td>
                    <td style={{ ...td, color: 'var(--muted)', fontFamily: 'monospace', fontSize: 10.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.method} {d.host}
                    </td>
                  </tr>
                  {open && (
                    <tr style={{ background: 'var(--surface)' }}>
                      <td colSpan={5} style={{ padding: '8px 14px 14px' }}>
                        <div style={{ fontSize: 10.5, color: 'var(--muted)', marginBottom: 6, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {row.url}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <tbody>
                            {d.flags.map((f, j) => (
                              <tr key={j} style={{ borderTop: '1px solid var(--line)' }}>
                                <td style={{ padding: '3px 8px', width: 200 }}>
                                  <span style={{ color: SEVERITY_COLOR[f.severity], fontWeight: 600 }}>{f.type}</span>
                                  <div style={{ color: 'var(--muted)', fontSize: 10 }}>{SEVERITY_LABEL[f.severity]}</div>
                                </td>
                                <td style={{ padding: '3px 8px', fontFamily: 'monospace', color: 'var(--muted)', width: 130 }}>{f.param}</td>
                                <td style={{ padding: '3px 8px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  <span style={{ color: f.value === '(EMPTY)' ? '#e5484d' : 'var(--ink)' }}>{f.value}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {feed.length === 0 && summary.meta.scanned > 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                No flagged requests match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Matrix */}
      {summary.vendors.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div
            onClick={() => setShowMatrix((s) => !s)}
            style={{ fontSize: 13, fontWeight: 700, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}
          >
            {showMatrix ? '▾' : '▸'} Disclosure matrix (vendor × data type)
          </div>
          {showMatrix && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 760, width: '100%' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)' }}>
                    <th style={{ ...th, position: 'sticky', left: 0, background: 'var(--surface)' }}>Vendor</th>
                    {MATRIX_TYPES.map(([lbl, , sev]) => (
                      <th key={lbl} style={{ ...th, textAlign: 'center' }}>
                        <span style={{ color: SEVERITY_COLOR[sev] }}>{lbl}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.vendors.map((v) => (
                    <tr key={v.vendor} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--bg)' }}>{v.vendor}</td>
                      {MATRIX_TYPES.map(([lbl, key, sev]) => (
                        <td key={lbl} style={{ ...td, textAlign: 'center' }}>
                          {v.dataTypes[key]
                            ? <span title={`${v.dataTypes[key]} request(s)`} style={{ color: SEVERITY_COLOR[sev], fontSize: 14 }}>●</span>
                            : <span style={{ color: 'var(--line)' }}>·</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 22, fontSize: 11, color: 'var(--muted)' }}>
        Detection parses OpenRTB / Amazon-DTB request bodies, query parameters, and headers. Identifier values are
        truncated. This mirrors the offline report in <code>qa-captures/TRACKERS-DATA-DISCLOSURES</code>.
      </div>

      <style>{`
        .dd-ctl {
          padding: 7px 10px; border: 1px solid var(--line); border-radius: 8px;
          font-size: 12px; background: var(--surface); color: var(--ink);
        }
      `}</style>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 12px', fontWeight: 600 };
const td = { padding: '7px 12px', verticalAlign: 'top' };
